import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Edit02Icon, Delete01Icon, PlusSignIcon, Search01Icon, FilterVerticalIcon } from 'hugeicons-react';
import api, { API_URL } from '../../../api';
import { toast } from 'react-hot-toast';
import StepByStepMeasureForm from '../../../components/forms/measures/StepByStepMeasureForm';
import DashboardContainer from '../../../components/dashboard/DashboardContainer';
import EditMeasureForm from '../../../components/forms/measures/EditMeasure';
import DeleteMeasure from '../../../components/forms/measures/DeleteMeasure';
import MeasureActionDialog from '../../../components/forms/measures/MeasureActionDialog';
import CopyMeasuresModal from '../../../components/forms/measures/CopyMeasuresModal';
import MeasureTooltip from '../../../components/common/MeasureTooltip';
import '../../../assets/css/pages/measures/measures.css';
import TableLoading from '../../../components/loading/TableLoading';
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { hasRole } from '../../../services/userPosition';
import EmptyState from '../../../components/common/EmptyState';
import { PrimaryButton, SecondaryButton } from '../../../components/forms/buttons';
import { TextInput } from '../../../components/forms/Input';

const MeasuresTable = ({ measures = [], onEditClick, onDeleteClick, onReorder, onSort, currentSort, onFilter, filters, selectedMeasures, onSelectMeasure, onSelectAll }) => {
  const handleDragEnd = (result) => {
    const { source, destination } = result;

    if (!destination || source.index === destination.index) return;

    const updatedMeasures = Array.from(measures);
    const [movedItem] = updatedMeasures.splice(source.index, 1);
    updatedMeasures.splice(destination.index, 0, movedItem);

    onReorder(updatedMeasures);
  };

  if (!measures || !Array.isArray(measures) || measures.length === 0) {
    return (
      <EmptyState
        icon="measure"
        title="No measures found"
        message="There are no measures to display for the selected filters."
        suggestion="Try adjusting your filters or add a new measure."
      />
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="measuresTable">
        {(provided) => (
          <table {...provided.droppableProps} ref={provided.innerRef}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Measure Name</th>
                <th>Category Name</th>
                <th>Group</th>
                {hasRole(["Admin", "Super User"]) ? <th>Actions</th> : <></>}
              </tr>
            </thead>
            <tbody>
              {measures.map((measure, index) => (
                <Draggable
                  key={measure.id?.toString() || `fallback-key-${index}`}
                  draggableId={measure.id?.toString() || `fallback-id-${index}`}
                  index={index}
                >
                  {(provided) => (
                    <tr
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    >
                      <td>{measure.synchronous_id}</td>
                      <td>
                        <MeasureTooltip
                          measureName={measure.name}
                          description={measure.description || measure.measure_description}
                        />
                      </td>
                      <td>{measure.category_name || measure.category}</td>
                      <td>{measure.group}</td>
                      {hasRole(["Admin", "Super User"]) ? (
                        <td>
                          <div className="action-buttons">
                            <Edit02Icon
                              size={34}
                              className="edit-icon"
                              onClick={() => onEditClick(measure.measure_id)}
                            />
                            <Delete01Icon
                              size={34}
                              className="delete-icon"
                              onClick={() => onDeleteClick(measure.measure_id)}
                            />
                          </div>
                        </td>
                      ) : <></>}
                    </tr>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </tbody>
          </table>
        )}
      </Droppable>
    </DragDropContext>
  );
};

const MeasurePageContent = () => {
  const [measures, setMeasures] = useState([]);
  const [nextPage, setNextPage] = useState(null);
  const [previousPage, setPreviousPage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const debounceTimeout = useRef(null);
  const [isSearching, setIsSearching] = useState(false);
  const [currentSort, setCurrentSort] = useState({ key: '', direction: 'asc' });
  const [selectedYear, setSelectedYear] = useState(null);
  const [years, setYears] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedUnit, setSelectedUnit] = useState('');
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterPopupRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorFetching, setErrorFetching] = useState(false);
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [selectedMeasureId, setSelectedMeasureId] = useState(null);
  const [editMeasureData, setEditMeasureData] = useState(null);
  const [measureToDelete, setMeasureToDelete] = useState(null);
  const [selectedMeasures, setSelectedMeasures] = useState([]);
  const filterButtonRef = useRef(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const yearsFetchedRef = useRef(false);
  const initialLoadRef = useRef(true);

  const fetchFilterOptions = useCallback(async () => {
    try {
      // Fetch categories
      const categoriesResponse = await api.get(`${API_URL}/categories/list/`);
      const categories = categoriesResponse.data || [];
      setCategoryOptions(categories.map(category => category.name));

      // Fetch measures data for groups and units
      const measuresResponse = await api.get(`${API_URL}/measures/`, {
        params: { page_size: 500, measure_year: selectedYear }
      });
      const measuresData = measuresResponse.data?.results || [];

      // Extract unique groups and units
      const groups = [...new Set(measuresData.map(m => m.group).filter(Boolean))];
      const units = [...new Set(measuresData.map(m => m.measure_unit).filter(Boolean))];

      setGroupOptions(groups);
      setUnitOptions(units);

    } catch (error) {
      console.error('Error fetching filter options:', error);
      if (error.response?.status !== 403) {
        toast.error('Failed to fetch filter options. Please try again later.');
      }
    }
  }, [selectedYear]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterPopupRef.current && !filterPopupRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };
    if (isFilterOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFilterOpen]);

  const [currentDateYear] = useState(new Date().getFullYear());

  const toggleFilter = () => {
    setIsFilterOpen(!isFilterOpen);
  };


  const fetchYears = useCallback(async () => {
    if (yearsFetchedRef.current) return null;

    try {
      console.log('Fetching available years...');
      const response = await api.get(`${API_URL}/measures/years/`);
      console.log('Years API Response:', response);

      let yearsData;
      if (Array.isArray(response.data)) {
        yearsData = response.data;
      } else if (response.data && response.data.years) {
        yearsData = response.data.years;
      } else if (response.data && response.data.results) {
        yearsData = response.data.results;
      } else {
        throw new Error('Invalid response format for years');
      }

      const validYears = yearsData
        .map(year => parseInt(year))
        .filter(year => !isNaN(year) && year > 0)
        .sort((a, b) => b - a);

      console.log('Processed years:', validYears);

      if (validYears.length === 0) {
        console.warn('No valid years found, using fallback years');
        const currentYear = new Date().getFullYear();
        const fallbackYears = [currentYear, currentYear - 1, currentYear - 2];
        setYears(fallbackYears);
        const selectedYearValue = fallbackYears[0];
        if (response?.status !== 403) {
          toast.error('No measure years found, using default years');
        }
        yearsFetchedRef.current = true; 
        return selectedYearValue;
      } else {
        setYears(validYears);
        const selectedYearValue = validYears.includes(new Date().getFullYear()) ? new Date().getFullYear() : validYears[0];
        yearsFetchedRef.current = true; 
        return selectedYearValue;
      }
    } catch (error) {
      console.error('Error fetching years:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });

      const currentYear = new Date().getFullYear();
      const fallbackYears = [currentYear, currentYear - 1, currentYear - 2];
      setYears(fallbackYears);
      const selectedYearValue = fallbackYears[0];
      if (error.response?.status !== 403) {
        toast.error('Failed to fetch years, using default years');
      }
      yearsFetchedRef.current = true;
      return selectedYearValue;
    }
  }, []);

  const fetchMeasuresData = useCallback(async (url = null, searchQuery = null, yearOverride = null) => {
    setIsLoading(true);
    setIsOverlayVisible(true);
    setErrorFetching(false);

    try {
      let response;
      const yearToUse = yearOverride !== null ? yearOverride : selectedYear;
      const params = {
        measure_year: yearToUse,
        ...(searchQuery && { search: searchQuery }),
        ...(selectedCategory && { category: selectedCategory }),
        ...(selectedGroup && { group: selectedGroup }),
        ...(selectedUnit && { measure_unit: selectedUnit })
      };

      if (url) {
        const urlObj = new URL(url);
        const relativePath = urlObj.pathname.substring(4) + urlObj.search; 
        response = await api.get(relativePath);
      } else {
        response = await api.get(`/measures/`, { params });
      }

      let measuresData;
      if (Array.isArray(response.data)) {
        measuresData = response.data;
        setNextPage(null);
        setPreviousPage(null);
      } else if (response.data && response.data.results) {
        measuresData = response.data.results;
        setNextPage(response.data.next);
        setPreviousPage(response.data.previous);
      } else if (response.data && typeof response.data === 'object') {
        measuresData = [response.data];
        setNextPage(null);
        setPreviousPage(null);
      } else {
        measuresData = [];
        setNextPage(null);
        setPreviousPage(null);
      }

      if (measuresData.length === 0) {
        // toast(`No measures found for ${yearToUse}`, { icon: 'ℹ️' });
      } else {
        console.log(measuresData.length)
      }

      setMeasures(measuresData);
    } catch (error) {
      if (error.response?.status === 404) {
        // Page doesn't exist, reset to empty
        setMeasures([]);
        setNextPage(null);
        setPreviousPage(null);
        toast.error('No more pages available.');
      } else if (error.response?.status === 403) {
        setMeasures([]);
        setNextPage(null);
        setPreviousPage(null);
        setErrorFetching(false);
      } else {
        setErrorFetching(true);
        setMeasures([]);
        setNextPage(null);
        setPreviousPage(null);
      }
    } finally {
      setIsLoading(false);
      setIsOverlayVisible(false);
    }
  }, [selectedYear, selectedCategory, selectedGroup, selectedUnit]);

  const handleChangePage = (url, direction) => {
    fetchMeasuresData(url);
    setCurrentPage((prev) => direction === 'next' ? prev + 1 : prev - 1);
  };

  const handleDataChanged = () => {
    fetchMeasuresData();
  };

  const removeMeasureFromState = (measureId) => {
    console.log('Removing measure from state:', measureId);
    setMeasures(prevMeasures => {
      const filteredMeasures = prevMeasures.filter(measure => measure.measure_id != measureId);
      console.log('Measures before:', prevMeasures.length, 'Measures after:', filteredMeasures.length);
      return filteredMeasures;
    });
  };

  const handleDataChangedPreserveFilters = () => {
    fetchMeasuresData(null, searchQuery);
  };

  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    if (!dataLoaded) {
      const loadData = async () => {
        setIsLoading(true);

      
        const selectedYearValue = await fetchYears();

       
        if (selectedYearValue !== null) {
          setSelectedYear(selectedYearValue);
        }

        await fetchFilterOptions();

        fetchMeasuresData(null, null, selectedYearValue || selectedYear);
        setDataLoaded(true);
        setIsInitialLoad(false);
      };

      loadData();
    }

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [dataLoaded, fetchYears, fetchMeasuresData, fetchFilterOptions, selectedYear]);

  useEffect(() => {
    if (dataLoaded && !isInitialLoad && !initialLoadRef.current) {
      const timeoutId = setTimeout(() => {
        fetchMeasuresData();
      }, 300);

      return () => clearTimeout(timeoutId);
    } else if (dataLoaded && initialLoadRef.current) {
      initialLoadRef.current = false;
    }
  }, [selectedYear, selectedCategory, selectedGroup, selectedUnit, dataLoaded, fetchMeasuresData, isInitialLoad]);


  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      fetchMeasuresData(null, searchQuery);
    }
  };

  const handleInputChange = (value) => {
    setSearchQuery(value);
    clearTimeout(debounceTimeout.current);
    debounceTimeout.current = setTimeout(() => {
      fetchMeasuresData(null, value);
    }, 300);
  };

  const handleSort = (config) => {
    setCurrentSort(config);
    const sortedData = [...measures].sort((a, b) => {
      const aVal = a[config.key] || '';
      const bVal = b[config.key] || '';
      if (typeof aVal === 'string') {
        return config.direction === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return config.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
    setMeasures(sortedData);
  };


  const handleNewMeasureClick = () => {
    setShowActionDialog(true);
  };

  return errorFetching ? (
    <div>Error fetching measures.</div>
  ) : (
    <div className="measures-page">
      <StepByStepMeasureForm
        isOpen={showNewForm}
        onClose={() => setShowNewForm(false)}
        onMeasureAdded={() => {
          setShowNewForm(false);
          handleDataChanged();
        }}
      />

      {showEditForm && selectedMeasureId && (
        <EditMeasureForm
          measureId={selectedMeasureId}
          measureData={editMeasureData}
          onClose={() => setShowEditForm(false)}
          year={selectedYear}
          onMeasureUpdated={() => {
            setShowEditForm(false);
            handleDataChanged();
          }}
        />
      )}

      {showDeletePopup && measureToDelete && (
        <DeleteMeasure
          measureId={measureToDelete}
          synchronousId={measures.find(m => m.measure_id === measureToDelete)?.synchronous_id}
          measureName={measures.find(m => m.measure_id === measureToDelete)?.name}
          isOpen={true}
          onClose={() => {
            setShowDeletePopup(false);
            setMeasureToDelete(null);
          }}
          removeMeasure={removeMeasureFromState}
        />
      )}

      <div className="measure-container">
        <div className="search-section">
          <TextInput
            iconClass={<Search01Icon />}
            type='search'
            placeholder='Search by measure name.....'
            value={searchQuery}
            setValue={handleInputChange}
            onKeyDown={handleKeyDown}
          />
        </div>
        <div className='buttons'>
          <SecondaryButton
            className="filter-button"
            onClick={toggleFilter}
            ref={filterButtonRef}
            iconClass={<FilterVerticalIcon />}
            buttonText='Filter'
          />

          {isFilterOpen && (
            <div
              className="filter-popup"
              ref={filterPopupRef}
              onClick={e => {
                if (e.target === filterPopupRef.current) setIsFilterOpen(false);
              }}
            >
                <div className="filter-section" onClick={e => e.stopPropagation()}>
                    <div className="filter-item">
                        <label>Year</label>
                        <select
                            className="year-select"
                            value={selectedYear}
                            onChange={(e) => setSelectedYear(e.target.value)}
                        >
                            {Array.isArray(years) && years.length > 0 ? (
                                years.map((year) => (
                                    <option key={year} value={year}>{year}</option>
                                ))
                            ) : (
                                <option value={selectedYear}>{selectedYear}</option>
                            )}
                        </select>
                    </div>
                    <div className="filter-item">
                        <label>Category</label>
                        <select
                            className="category-select"
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                        >
                            <option value="">All Categories</option>
                            {categoryOptions.map((category) => (
                                <option key={category} value={category}>{category}</option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-item">
                        <label>Group</label>
                        <select
                            className="group-select"
                            value={selectedGroup}
                            onChange={(e) => setSelectedGroup(e.target.value)}
                        >
                            <option value="">All Groups</option>
                            {groupOptions.map((group) => (
                                <option key={group} value={group}>{group}</option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-item">
                        <label>Unit Type</label>
                        <select
                            className="unit-select"
                            value={selectedUnit}
                            onChange={(e) => setSelectedUnit(e.target.value)}
                        >
                            <option value="">All Unit Types</option>
                            {unitOptions.map((unit) => (
                                <option key={unit} value={unit}>{unit}</option>
                            ))}
                        </select>
                    </div>
                   
                    <button className="reset-button" onClick={() => {
                        setSelectedYear(currentDateYear);
                        setSelectedCategory('');
                        setSelectedGroup('');
                        setSelectedUnit('');
                        setIsFilterOpen(false);
                        fetchMeasuresData();
                    }}>Reset All Filters</button>
                    <button className="close-button" onClick={() => {
                        setIsFilterOpen(false);
                    }}>Close</button>
                </div>
            </div>
          )}

          <div className="action">
            {hasRole(["Admin", "Super User"]) && (
              <PrimaryButton
                iconClass={<PlusSignIcon />}
                buttonText='Add New Measure'
                onClick={() => setShowActionDialog(true)}
              />
            )}
          </div>
              {(selectedYear || selectedCategory || selectedGroup || selectedUnit) && (
        <div className="filter-summary">
          {/* <span className="filter-label"><h3>Active Filters:</h3></span> */}
          <div className="filter-tags">
            {selectedYear && (

              <span className="filter-tag">
                <h3>Year:</h3>
                <span>{selectedYear}</span>
              </span>
            )}
            {selectedCategory && (
              <span className="filter-tag">
                <h3>Category:</h3>
                <span>{selectedCategory}</span>
              </span>
            )}
            {selectedGroup && (
              <span className="filter-tag">
                <h3>Group:</h3>
                <span>{selectedGroup}</span>
              </span>
            )}
            {selectedUnit && (
              <span className="filter-tag">
                <h3>Unit:</h3>
                <span>{selectedUnit}</span>
              </span>
            )}
          </div>
          <p className="clear-all-filters"
            onClick={() => {
              setSelectedYear(currentDateYear);
              setSelectedCategory('');
              setSelectedGroup('');
              setSelectedUnit('');
              fetchMeasuresData();
            }}>Clear All</p>
        </div>
      )}
        </div>
      
      </div>

    

      <div className="measures table-container">
        {isOverlayVisible && <TableLoading />}
        {isLoading ? <TableLoading /> : (
          measures.length === 0 ? (
            <EmptyState
              icon="measure"
              title="No measures found"
              message="No measures found for the selected filters."
              suggestion="Try adjusting your filters or add a new measure."
            />
          ) : (
            <>
            <MeasuresTable
              measures={measures}
              onEditClick={id => {
                const measure = measures.find(m => m.measure_id === id);
                setSelectedMeasureId(id);
                setEditMeasureData(measure);
                setShowEditForm(true);
              }}
              onDeleteClick={id => {
                setMeasureToDelete(id);
                setShowDeletePopup(true);
              }}
              onReorder={setMeasures}
              onSort={handleSort}
              currentSort={currentSort}
              onFilter={() => {}}
              filters={{}}
            />
            <div className="pagination-controls" >
              <button className={`previous ${!previousPage ? 'disabled' : ''}`} onClick={() => handleChangePage(previousPage, 'prev')} disabled={!previousPage}>Previous</button>
              <span>Page {currentPage}</span>
              <button className={`next ${!nextPage ? 'disabled' : ''}`} onClick={() => handleChangePage(nextPage, 'next')} disabled={!nextPage}>Next</button>
            </div>
            </>
          )
        )}
      </div>

      <MeasureActionDialog
        isOpen={showActionDialog}
        onClose={() => setShowActionDialog(false)}
        onCreateNew={() => {
          setShowActionDialog(false);
          setShowNewForm(true);
        }}
        onCopyMeasures={() => {
          setShowActionDialog(false);
          setShowCopyModal(true);
        }}
      />
      {showCopyModal && (
        <CopyMeasuresModal
          open
          onClose={() => setShowCopyModal(false)}
          measures={measures}
          currentYear={selectedYear}
          onActionComplete={() => {
            setShowCopyModal(false);
            handleDataChanged();
          }}
        />
      )}
    </div>
  );
};

const MeasurePage = () => {
  return (
    <DashboardContainer content={<MeasurePageContent />} pageTitle={'Measure'} />
  );
};

export default MeasurePage;